import { useEffect, useMemo, useState } from 'react'

import { LimitedTable } from 'components/LimitedTable/LimitedTable'
import { PageSection } from 'components/PageSection/PageSection'
import { useChannel } from 'hooks/useChannel'
import { useTableState } from 'hooks/useTableState'
import { useTimeRangeParams } from 'hooks/useTimeRangeParams'
import { useAgentAttendanceDetailsQuery } from 'modules/api/analytics/queries'
import { UrlStateStorageKeys } from 'modules/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/url-state-storage/useHashedSearchParams'
import { GlobalFilterState } from 'pages/Monitoring/GlobalFilters/GlobalFilters'
import { AgentTicketsRequest } from 'types'
import { deriveLoadingStateFromQueryStatus } from 'utils/deriveLoadingStateFromQueryStatus'
import { convertTimeStringToDate } from 'utils/time/convertTimeStringToDate'

import { AgentTicketsSide } from './AgentTicketsSide'
import { getColumns, TicketSideData } from './columns'
import { ShiftTimelineSide } from './components/ShiftTimelineSide'
import { prepareTableData } from './utils/prepareTableData'

export const AgentAttendanceDetails = () => {
  const { entityName, channel, regionedChannel } = useChannel()

  const { state } = useHashedSearchParams<GlobalFilterState>({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })
  const { from, to, start, end } = useTimeRangeParams({
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
    contextKey: regionedChannel,
  })
  const {
    state: tableState,
    setSorting,
    setColumnFilters,
  } = useTableState(UrlStateStorageKeys.AgentAttendanceDetailsState, regionedChannel)

  const queueId = state.queues
  const agentId = state.agents
  const specialisationId = state.specialisations
  const departmentId = state.departments
  const areaId = state.areas

  const [selectedTicketsSideData, setSelectedTicketsSideData] = useState<TicketSideData>()
  const [selectedAdherenceShiftData, setSelectedAdherenceShiftData] =
    useState<TicketSideData>()

  const ticketsRequestParams: AgentTicketsRequest = useMemo(
    () => ({
      agentId: selectedTicketsSideData?.agent?.id ?? '',
      channel,
      departmentId,
      areaId,
      queueId,
      specialisationId,
    }),
    [
      areaId,
      channel,
      departmentId,
      queueId,
      selectedTicketsSideData?.agent?.id,
      specialisationId,
    ],
  )

  const { data: agentAttendanceDetails = [], status } = useAgentAttendanceDetailsQuery({
    from,
    to,
    start: convertTimeStringToDate({
      timeStr: start,
      useUtc: true,
      shouldReturnDate: false,
      shouldReturnDefaultTime: false,
    }),
    end: convertTimeStringToDate({
      timeStr: end,
      useUtc: true,
      shouldReturnDate: false,
      shouldReturnDefaultTime: false,
    }),
    departmentId,
    areaId,
    specialisationId,
    queueId,
    agentId,
    channel: [channel],
  })

  const preparedData = useMemo(
    () => prepareTableData(agentAttendanceDetails),
    [agentAttendanceDetails],
  )
  const columns = useMemo(
    () =>
      getColumns({
        selectedTicketsSideData,
        selectedAdherenceShiftData,
        entityName,
        agentAttendanceDetails: preparedData,
        onShowTickets: setSelectedTicketsSideData,
        onShowAdherenceTimeline: setSelectedAdherenceShiftData,
      }),
    [selectedAdherenceShiftData, preparedData, selectedTicketsSideData, entityName],
  )

  useEffect(() => {
    if (channel) {
      setSelectedTicketsSideData(undefined)
    }
  }, [channel])

  return (
    <PageSection title="Agent attendance details">
      <LimitedTable
        state={tableState}
        rowsToShow={12}
        primaryColumnId="name"
        data={preparedData}
        columns={columns}
        rowHeight="medium"
        aria-label="Agent attendance table"
        loadingState={deriveLoadingStateFromQueryStatus(status)}
        expandable
        resizable
        showingCount
        enableMultiSort
        onSortingChange={setSorting}
        onColumnFiltersChange={setColumnFilters}
      />

      <AgentTicketsSide
        open={Boolean(selectedTicketsSideData)}
        subtitle={selectedTicketsSideData?.agent?.name}
        ticketsRequestParams={ticketsRequestParams}
        onClose={() => setSelectedTicketsSideData(undefined)}
      />

      <ShiftTimelineSide
        shiftId={selectedAdherenceShiftData?.shiftId}
        agent={selectedAdherenceShiftData?.agent}
        open={Boolean(selectedAdherenceShiftData)}
        onClose={() => setSelectedAdherenceShiftData(undefined)}
      />
    </PageSection>
  )
}
